import { css, cx } from '@linaria/atomic';
import { theme } from '@frontend/theme';
import { rgba } from '../../helpers/color/rgba';

const _chipVariantOptions = [
  'primary',
  'critical',
  'success',
  'warn',
  'active',
  'eggplant',
  'seaweed',
  'disabled',
  'interactive',
  'neutral',
  'neutral1',
  'outline',
  'default',
  'primaryDark',
  'warningDark',
  'warningNeutral',
  'indigo',
  'tangerine',
  'rose',
  'task',
  'done',
] as const;
export type Variants = (typeof _chipVariantOptions)[number];

const _tagColorOptions = ['blue', 'green', 'teal', 'yellow', 'red', 'purple', 'gray'] as const;
export type TagColor = (typeof _tagColorOptions)[number];

const _statusSwitcherVariantOptions = [
  'default',
  'success',
  'warning',
  'critical',
  'seaweed',
  'eggplant',
  'neutral',
  'disabled',
] as const;
export type StatusSwitcherVariants = (typeof _statusSwitcherVariantOptions)[number];

const _categoryChipVariantOptions = [
  'blue',
  'indigo',
  'eggplant',
  'rose',
  'red',
  'tangerine',
  'yellow',
  'green',
  'seaweed',
  'neutral',
  'disabled',
] as const;
export type CategoryChipVariants = (typeof _categoryChipVariantOptions)[number];

export type ChipStylesProps = {
  variant: Variants;
  size: 'tiny' | 'default' | 'large';
  isResponsive?: boolean;
  maxWidth: string;
};

export const variantStylesMap: Record<Variants, string> = {
  primary: css`
    background: ${theme.colors.primary10};
    border-radius: ${theme.borderRadius.small};
    color: ${theme.colors.primary50};
  `,
  critical: css`
    background: ${theme.colors.critical10};
    color: ${theme.colors.critical50};
  `,
  success: css`
    background: ${theme.colors.success10};
    color: ${theme.colors.success50};
  `,
  warn: css`
    background: ${theme.colors.warning5};
    color: ${theme.colors.warning80};
  `,
  active: css`
    background: ${theme.colors.warning5};
    color: ${theme.colors.warning70};
  `,
  eggplant: css`
    background: ${theme.colors.secondary.eggplant10};
    color: ${theme.colors.secondary.eggplant50};
  `,
  seaweed: css`
    background: ${theme.colors.secondary.seaweed10};
    color: ${theme.colors.secondary.seaweed50};
  `,
  disabled: css`
    background: ${theme.colors.neutral10};
    color: ${theme.colors.neutral30};
  `,
  interactive: css`
    background-color: transparent;
    border: 1px solid ${theme.colors.primary50};
    color: ${theme.colors.primary50};
  `,
  neutral: css`
    background: ${theme.colors.neutral10};
    color: ${theme.colors.neutral50};
  `,
  neutral1: css`
    background: ${rgba(theme.colors.neutral90, 0.1)};
    color: ${theme.colors.neutral70};
  `,
  outline: css`
    border: 1px solid ${theme.colors.neutral50};
    color: ${theme.colors.neutral50};
  `,
  default: css`
    background: ${theme.colors.neutral5};
    color: ${theme.colors.neutral50};
  `,
  primaryDark: css`
    background: ${theme.colors.primary20};
    color: ${theme.colors.primary80};
  `,
  warningDark: css`
    background: ${theme.colors.warning50};
    color: ${theme.colors.warning80};
  `,
  warningNeutral: css`
    background: ${theme.colors.warning20};
    color: ${theme.colors.warning70};
  `,
  indigo: css`
    background: ${theme.colors.indigo10};
    color: ${theme.colors.indigo60};
  `,
  tangerine: css`
    background: ${theme.colors.tangerine10};
    color: ${theme.colors.tangerine60};
  `,
  rose: css`
    background: ${theme.colors.rose10};
    color: ${theme.colors.rose60};
  `,
  task: css`
    background: ${theme.colors.tangerine40};
    color: ${theme.colors.white};
  `,
  done: css`
    background: ${theme.colors.success5};
    color: ${theme.colors.success80};
  `,
};

const TAG_CHIP_CLICKABLE = css`
  cursor: pointer;
`;

const TAG_CHIP_DEFAULT = css`
  cursor: default;
`;

export const tagVariantStylesMap: Record<TagColor, { styles: string; hoverStyles: string }> = {
  blue: {
    styles: css`
      background-color: ${theme.colors.primary5};
      border: 1px solid ${theme.colors.primary70};
      color: ${theme.colors.primary70};
      & svg {
        fill: ${theme.colors.primary70};
      }
    `,
    hoverStyles: css`
      &:hover {
        background-color: ${theme.colors.primary10};
      }
    `,
  },
  green: {
    styles: css`
      background-color: ${theme.colors.success5};
      border: 1px solid ${theme.colors.success70};
      color: ${theme.colors.success70};
      & svg {
        fill: ${theme.colors.success70};
      }
    `,
    hoverStyles: css`
      &:hover {
        background-color: ${theme.colors.success10};
      }
    `,
  },
  teal: {
    styles: css`
      background-color: ${theme.colors.secondary.seaweed5};
      border: 1px solid ${theme.colors.secondary.seaweed70};
      color: ${theme.colors.secondary.seaweed70};
      & svg {
        fill: ${theme.colors.secondary.seaweed70};
      }
    `,
    hoverStyles: css`
      &:hover {
        background-color: ${theme.colors.secondary.seaweed10};
      }
    `,
  },
  yellow: {
    styles: css`
      background-color: ${theme.colors.warning5};
      border: 1px solid ${theme.colors.warning80};
      color: ${theme.colors.warning80};
      & svg {
        fill: ${theme.colors.warning80};
      }
    `,
    hoverStyles: css`
      &:hover {
        background-color: ${theme.colors.warning50};
      }
    `,
  },
  red: {
    styles: css`
      background-color: ${theme.colors.critical5};
      border: 1px solid ${theme.colors.critical70};
      color: ${theme.colors.critical70};
      & svg {
        fill: ${theme.colors.critical70};
      }
    `,
    hoverStyles: css`
      &:hover {
        background-color: ${theme.colors.critical10};
      }
    `,
  },
  purple: {
    styles: css`
      background-color: ${theme.colors.secondary.eggplant5};
      border: 1px solid ${theme.colors.secondary.eggplant70};
      color: ${theme.colors.secondary.eggplant70};
      & svg {
        fill: ${theme.colors.secondary.eggplant70};
      }
    `,
    hoverStyles: css`
      &:hover {
        background-color: ${theme.colors.secondary.eggplant10};
      }
    `,
  },
  gray: {
    styles: css`
      background-color: ${theme.colors.neutral10};
      border: 1px solid ${theme.colors.neutral70};
      color: ${theme.colors.neutral70};
      & svg {
        fill: ${theme.colors.neutral70};
      }
    `,
    hoverStyles: css`
      &:hover {
        background-color: ${theme.colors.neutral20};
      }
    `,
  },
};

export type TagChipStylesProps = {
  color: TagColor;
  hasOnClick: boolean;
};

export const statusSwitcherVariantColors: Record<
  StatusSwitcherVariants,
  {
    color: string;
    background: string;
    hoverBackground: string;
    borderColor: string;
  }
> = {
  default: {
    color: theme.colors.neutral90,
    background: theme.colors.white,
    hoverBackground: theme.colors.neutral10,
    borderColor: theme.colors.neutral60,
  },
  success: {
    color: theme.colors.success60,
    background: theme.colors.success5,
    hoverBackground: theme.colors.success10,
    borderColor: theme.colors.success60,
  },
  warning: {
    color: theme.colors.warning60,
    background: theme.colors.warning5,
    hoverBackground: theme.colors.warning10,
    borderColor: theme.colors.warning60,
  },
  critical: {
    color: theme.colors.critical60,
    background: theme.colors.critical5,
    hoverBackground: theme.colors.critical10,
    borderColor: theme.colors.critical60,
  },
  seaweed: {
    color: theme.colors.secondary.seaweed60,
    background: theme.colors.secondary.seaweed5,
    hoverBackground: theme.colors.secondary.seaweed10,
    borderColor: theme.colors.secondary.seaweed60,
  },
  eggplant: {
    color: theme.colors.secondary.eggplant60,
    background: theme.colors.secondary.eggplant5,
    hoverBackground: theme.colors.secondary.eggplant10,
    borderColor: theme.colors.secondary.eggplant60,
  },
  neutral: {
    color: theme.colors.neutral60,
    background: theme.colors.neutral5,
    hoverBackground: theme.colors.neutral10,
    borderColor: theme.colors.neutral60,
  },
  disabled: {
    color: theme.colors.neutral20,
    background: theme.colors.neutral5,
    borderColor: 'transparent',
    hoverBackground: theme.colors.neutral5,
  },
};

export const categoryChipVariantColors: Record<CategoryChipVariants, { color: string }> = {
  blue: { color: theme.colors.primary50 },
  indigo: { color: theme.colors.indigo40 },
  eggplant: { color: theme.colors.secondary.eggplant50 },
  rose: { color: theme.colors.rose40 },
  red: { color: theme.colors.critical50 },
  tangerine: { color: theme.colors.tangerine40 },
  yellow: { color: theme.colors.warning20 },
  green: { color: theme.colors.success40 },
  seaweed: { color: theme.colors.secondary.seaweed40 },
  neutral: { color: theme.colors.neutral40 },
  disabled: { color: theme.colors.neutral20 },
};
const _timezoneChipVariantOptions = ['neutral', 'neutralDark'] as const;
export type TimezoneChipVariant = (typeof _timezoneChipVariantOptions)[number];
const _taskChipVariantOptions = ['task', 'done'] as const;
export type TaskChipVariant = (typeof _taskChipVariantOptions)[number];

// Chip Base
export const CHIP_BASE = css`
  align-items: center;
  border-radius: ${theme.borderRadius.small};
  background: ${theme.colors.white};
  display: inline-flex;
  justify-content: center;
  gap: ${theme.spacing(0.75)};
  height: 24px;
  max-width: 120px;
  padding: ${theme.spacing(0.25, 1)};
  white-space: nowrap;
  font-size: ${theme.font.size.medium};

  .chip-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin: 0;
  }

  &:focus {
    outline: 2px solid ${theme.colors.primary50};
    outline-offset: 1px;
  }

  @supports (selector(:has(*))) {
    &:focus-within:has(input) {
      outline: 2px solid ${theme.colors.primary50};
      outline-offset: 1px;
    }
  }
  @supports not (selector(:has(*))) {
    &:focus-within {
      outline: 2px solid ${theme.colors.primary50};
      outline-offset: 1px;
    }
  }

  button {
    color: inherit;

    &:focus {
      outline: 2px solid ${theme.colors.primary50};
      border-radius: ${theme.borderRadius.small};
    }
  }

  svg:first-of-type {
    flex-shrink: 0;
  }
`;
const CHIP_DEFAULT = css``;

const CHIP_LARGE = css`
  height: 40px;
`;

const CHIP_TINY = css`
  font-size: ${theme.fontSize(12)};
  padding: 0 ${theme.spacing(0.5)};
  height: ${theme.spacing(2)};
`;

const CHIP_SIZE_CLASS = {
  default: CHIP_DEFAULT,
  large: CHIP_LARGE,
  tiny: CHIP_TINY,
} as const;

// Dropdown Filter Chip
const DROPDOWN_FILTER_BASE = css`
  cursor: pointer;

  & .location-caret {
    transition: transform 250ms ease-in-out;
  }
`;

const DROPDOWN_FILTER_ACTIVE = css`
  background-color: ${theme.colors.primary50};
  border: 1px solid ${theme.colors.primary50};

  * {
    color: ${theme.colors.white};
    fill: ${theme.colors.white};
  }
`;

const DROPDOWN_FILTER_INACTIVE = css`
  background-color: transparent;
  border: 1px solid ${theme.colors.neutral50};

  * {
    color: ${theme.colors.neutral90};
    fill: ${theme.colors.neutral90};
  }
`;

const DROPDOWN_FILTER_OPEN = css`
  & .location-caret {
    transform: rotate(180deg);
  }
`;

export const getDropdownFilterChipClassName = ({
  filterIsActive,
  isOpen,
}: {
  filterIsActive: boolean;
  isOpen: boolean;
}) =>
  cx(
    DROPDOWN_FILTER_BASE,
    filterIsActive ? DROPDOWN_FILTER_ACTIVE : DROPDOWN_FILTER_INACTIVE,
    isOpen && DROPDOWN_FILTER_OPEN
  );

// Category Chip
const CATEGORY_CHIP_BASE = css`
  background-color: ${theme.colors.neutral5};
  color: ${theme.colors.neutral90};
  gap: ${theme.spacing(0.5)};
  max-width: none;

  .category-chip-indicator {
    width: ${theme.spacing(1)};
    height: ${theme.spacing(1)};
    border-radius: 50%;
  }
`;

const CATEGORY_CHIP_DISABLED = css`
  color: ${theme.colors.neutral20};
`;

export const getCategoryChipClassName = (variant: CategoryChipVariants) =>
  cx(CATEGORY_CHIP_BASE, variant === 'disabled' && CATEGORY_CHIP_DISABLED);

// TimeZone Chip
const TIMEZONE_CHIP_BASE = css`
  height: 16px;
  padding: ${theme.spacing(0.25)};
  gap: ${theme.spacing(0.25)};
`;

const TIMEZONE_CHIP_NEUTRAL = css`
  background: ${theme.colors.neutral20};

  & > svg {
    color: ${theme.colors.neutral50};
  }

  .chip-text {
    color: ${theme.colors.neutral50};
    font-weight: ${theme.font.weight.bold};
    font-size: ${theme.font.size.small};
  }
`;

const TIMEZONE_CHIP_NEUTRAL_DARK = css`
  background: ${theme.colors.neutral30};

  & > svg {
    color: ${theme.colors.neutral90};
  }

  .chip-text {
    color: ${theme.colors.neutral90};
    font-weight: ${theme.font.weight.bold};
    font-size: ${theme.font.size.small};
  }
`;

const TIMEZONE_CHIP_VARIANT_CLASS = {
  neutral: TIMEZONE_CHIP_NEUTRAL,
  neutralDark: TIMEZONE_CHIP_NEUTRAL_DARK,
} as const;

export const getTimeZoneChipClassName = (variant: TimezoneChipVariant) =>
  cx(TIMEZONE_CHIP_BASE, TIMEZONE_CHIP_VARIANT_CLASS[variant]);

//SwitchFilter Chip
const SWITCH_FILTER_BASE = css`
  max-width: 144px;
  width: fit-content;
  padding: ${theme.spacing(1)};
  cursor: pointer;
  height: ${theme.spacing(5)};
  color: ${theme.colors.neutral90};
  background-color: ${theme.colors.white};
  outline: 1px solid ${theme.colors.neutral50};
  border: 0;

  .chip-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin: 0;
  }

  &:disabled {
    background-color: ${theme.colors.white};
    color: ${theme.colors.neutral20};
    outline: 1px solid ${theme.colors.neutral20};
  }

  &:focus {
    background-color: ${theme.colors.white};
    border: 1px solid ${theme.colors.neutral90};
    border-radius: 8px;
  }
`;

const SWITCH_FILTER_UNSELECTED = css`
  &:hover {
    background-color: ${theme.colors.neutral10};
    outline: 0;
  }
`;

const SWITCH_FILTER_SELECTED = css`
  background-color: ${theme.colors.primary5};
  border: 1px solid ${theme.colors.primary40};

  &:hover {
    background-color: ${theme.colors.primary20};
    outline: 1px solid ${theme.colors.primary5};
  }
`;

export const getSwitchFilterChipClassName = (isSelected?: boolean) =>
  cx(SWITCH_FILTER_BASE, isSelected ? SWITCH_FILTER_SELECTED : SWITCH_FILTER_UNSELECTED);

//Task Chip
const TASK_CHIP_BASE = css`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing(0.5)};
  width: auto;
`;

const TASK_CHIP_TASK = css`
  background: ${theme.colors.tangerine40};
  border: 1px solid ${theme.colors.tangerine50};

  & > svg {
    color: ${theme.colors.white};
    flex-shrink: 0;
  }

  .chip-text {
    color: ${theme.colors.white};
    font-weight: ${theme.font.weight.bold};
    max-width: 70px;
  }

  span {
    color: ${theme.colors.white};
    font-weight: ${theme.font.weight.bold};
  }
`;

const TASK_CHIP_DONE = css`
  background: ${theme.colors.success5};
  border: 1px solid ${theme.colors.success50};

  & > svg {
    color: ${theme.colors.success80};
    flex-shrink: 0;
  }

  .chip-text {
    color: ${theme.colors.success80};
    font-weight: ${theme.font.weight.bold};
    max-width: 70px;
  }

  span {
    color: ${theme.colors.success80};
    font-weight: ${theme.font.weight.bold};
  }
`;

const TASK_CHIP_VARIANT_CLASS = {
  task: TASK_CHIP_TASK,
  done: TASK_CHIP_DONE,
} as const;

export const getTaskChipClassName = (variant: TaskChipVariant) => cx(TASK_CHIP_BASE, TASK_CHIP_VARIANT_CLASS[variant]);

// Status Switcher Chip
const STATUS_SWITCHER_DEFAULT = css`
  background: ${theme.colors.white};
  color: ${theme.colors.neutral90};
  border: 1px solid ${theme.colors.neutral60};

  &:hover {
    background-color: ${theme.colors.neutral10};
    border-color: transparent;
  }

  &:focus {
    border: 1px solid ${theme.colors.neutral90};
    background: ${theme.colors.white};
  }
`;

const STATUS_SWITCHER_SUCCESS = css`
  background: ${theme.colors.success5};
  color: ${theme.colors.success60};
  border: 1px solid ${theme.colors.success60};

  &:hover {
    background-color: ${theme.colors.success10};
    border-color: transparent;
  }

  &:focus {
    border: 1px solid ${theme.colors.success60};
    background: ${theme.colors.success5};
  }
`;
const STATUS_SWITCHER_WARNING = css`
  background: ${theme.colors.warning5};
  color: ${theme.colors.warning60};
  border: 1px solid ${theme.colors.warning60};

  &:hover {
    background-color: ${theme.colors.warning10};
    border-color: transparent;
  }

  &:focus {
    border: 1px solid ${theme.colors.warning60};
    background: ${theme.colors.warning5};
  }
`;

const STATUS_SWITCHER_CRITICAL = css`
  background: ${theme.colors.critical5};
  color: ${theme.colors.critical60};
  border: 1px solid ${theme.colors.critical60};

  &:hover {
    background-color: ${theme.colors.critical10};
    border-color: transparent;
  }

  &:focus {
    border: 1px solid ${theme.colors.critical60};
    background: ${theme.colors.critical5};
  }
`;

const STATUS_SWITCHER_SEAWEED = css`
  background: ${theme.colors.secondary.seaweed5};
  color: ${theme.colors.secondary.seaweed60};
  border: 1px solid ${theme.colors.secondary.seaweed60};

  &:hover {
    background-color: ${theme.colors.secondary.seaweed10};
    border-color: transparent;
  }

  &:focus {
    border: 1px solid ${theme.colors.secondary.seaweed60};
    background: ${theme.colors.secondary.seaweed5};
  }
`;

const STATUS_SWITCHER_EGGPLANT = css`
  background: ${theme.colors.secondary.eggplant5};
  color: ${theme.colors.secondary.eggplant60};
  border: 1px solid ${theme.colors.secondary.eggplant60};

  &:hover {
    background-color: ${theme.colors.secondary.eggplant10};
    border-color: transparent;
  }

  &:focus {
    border: 1px solid ${theme.colors.secondary.eggplant60};
    background: ${theme.colors.secondary.eggplant5};
  }
`;

const STATUS_SWITCHER_NEUTRAL = css`
  background: ${theme.colors.neutral5};
  color: ${theme.colors.neutral60};
  border: 1px solid ${theme.colors.neutral60};

  &:hover {
    background-color: ${theme.colors.neutral10};
    border-color: transparent;
  }

  &:focus {
    border: 1px solid ${theme.colors.neutral60};
    background: ${theme.colors.neutral5};
  }
`;

const STATUS_SWITCHER_DISABLED = css`
  background: ${theme.colors.neutral5};
  color: ${theme.colors.neutral20};
  border: 1px solid transparent;
`;

const STATUS_SWITCHER_VARIANT_CLASS = {
  default: STATUS_SWITCHER_DEFAULT,
  success: STATUS_SWITCHER_SUCCESS,
  warning: STATUS_SWITCHER_WARNING,
  critical: STATUS_SWITCHER_CRITICAL,
  seaweed: STATUS_SWITCHER_SEAWEED,
  eggplant: STATUS_SWITCHER_EGGPLANT,
  neutral: STATUS_SWITCHER_NEUTRAL,
  disabled: STATUS_SWITCHER_DISABLED,
} as const;

const STATUS_SWITCHER_BASE = css`
  cursor: pointer;
  max-width: 114px;
  font-size: ${theme.font.size.small};
  gap: ${theme.spacing(0.5)};
`;

export const getStatusSwitcherChipClassName = (variant: StatusSwitcherVariants) =>
  cx(STATUS_SWITCHER_BASE, STATUS_SWITCHER_VARIANT_CLASS[variant]);

export const CHIP_EDITABLE = css`
  width: max-content;
`;

export const CHIP_TEXT = css`
  font-weight: 400;
`;

export const EDITABLE_WRAPPER = css`
  display: inline-grid;

  &::after {
    content: attr(data-value);
    visibility: hidden;
    white-space: pre-wrap;
    height: 1px;
    width: auto;
    min-width: 1em;
    grid-area: 1;
    font: inherit;
    padding: 0;
    margin: 0;
    resize: none;
    background: none;
    appearance: none;
    border: none;
  }
`;

export const EDITABLE_INPUT = css`
  width: auto;
  min-width: 1em;
  grid-area: 1;
  font: inherit;
  padding: 0;
  margin: 0;
  resize: none;
  background: none;
  appearance: none;
  border: none;

  &:focus {
    border: none;
    outline: none;
  }
`;

export const REMOVABLE_CHIP_BUTTON = css`
  display: flex;
  flex-shrink: 0;
  padding: ${theme.spacing(0.5)};
  border-radius: ${theme.borderRadius.small};

  &:hover {
    background: ${theme.colors.white};
  }
`;

export const REMOVABLE_CHIP_BUTTON_DISABLED = css`
  pointer-events: none;
`;

//Tag Chip
export const TAG_REMOVABLE_BUTTON = css`
  display: flex;
`;

export const getTagVariantClassName = (color: TagColor) => tagVariantStylesMap[color]?.styles;

export const getTagHoverClassName = (color: TagColor) => tagVariantStylesMap[color]?.hoverStyles;

export const getTagChipClassName = (color: TagColor, hasOnClick: boolean) =>
  cx(
    hasOnClick ? TAG_CHIP_CLICKABLE : TAG_CHIP_DEFAULT,
    getTagVariantClassName(color),
    hasOnClick && getTagHoverClassName(color)
  );

export const getChipSizeClassName = (size: ChipStylesProps['size']) => CHIP_SIZE_CLASS[size];
export const getChipVariantClassName = (variant: Variants) => variantStylesMap[variant];

export const DISPLAY_FLEX = css`
  display: flex;
`;
